import {
  ColorInput,
  FileInput,
  Group,
  Image,
  Stack,
  Title,
} from "@mantine/core"
import { IconExternalLink } from "@tabler/icons-react"
import { Button } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { OrganizationsResponses } from "@tfg/types/endpoints/organizations"

import { useUpdateOutPutCustomizationMutation } from "~/pages/Brand/OrganizationSettings/ProductPages/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

interface Props {
  displaySetting: OrganizationsResponses.Organization["settings"]["displaySettings"][number]
}

export const AdvancedDisplaySettings = ({ displaySetting }: Props) => {
  const { user } = useAuth()
  const { t } = useTranslation("organizationSettings")
  const { mutate: updateDisplaySettings, isLoading } =
    useUpdateOutPutCustomizationMutation(
      displaySetting.id,
      user?.organization?.id
    )

  const [outputBackgroundColor, setOutputBackgroundColor] = useState(
    displaySetting.outputBackgroundColor ?? "#F6F2EE"
  )
  const [outputHeaderBackgroundColor, setOutputHeaderBackgroundColor] =
    useState(displaySetting.outputHeaderBackgroundColor ?? "#6F9382")
  const [outputTextBodyColor, setOutputTextBodyColor] = useState(
    displaySetting.outputTextBodyColor ?? "#000000"
  )
  const [outputTitlesColor, setOutputTitlesColor] = useState(
    displaySetting.outputTitlesColor ?? "#000000"
  )

  const [outputLogoFile, setOutputLogoFile] = useState<File | null>(null)
  return (
    <Stack gap="var(--tfg-molecule-padding-y)">
      {displaySetting.outputLogoUrl ? (
        <Image src={displaySetting.outputLogoUrl} width="250px" />
      ) : (
        <Title>{displaySetting.slug}</Title>
      )}
      <Group>
        <FileInput
          placeholder={t("outputs.logoPlaceholder")}
          accept="image/png,image/jpeg"
          label={t("outputs.logo")}
          value={outputLogoFile}
          onChange={setOutputLogoFile}
        />
      </Group>
      <Group gap="var(--tfg-molecule-padding-x)">
        <ColorInput
          value={outputBackgroundColor}
          onChange={setOutputBackgroundColor}
          label={t("outputs.backgroundColor")}
        />
        <ColorInput
          value={outputHeaderBackgroundColor}
          onChange={setOutputHeaderBackgroundColor}
          label={t("outputs.headerBackgroundColor")}
        />
        <ColorInput
          value={outputTextBodyColor}
          onChange={setOutputTextBodyColor}
          label={t("outputs.textColor")}
        />
        <ColorInput
          value={outputTitlesColor}
          onChange={setOutputTitlesColor}
          label={t("outputs.titleColor")}
        />
      </Group>
      <Group justify="space-between" gap="var(--tfg-molecule-padding-x)">
        <Button
          loading={isLoading}
          onClick={() =>
            updateDisplaySettings({
              update: {
                outputBackgroundColor,
                outputHeaderBackgroundColor,
                outputTextBodyColor,
                outputTitlesColor,
              },
              outputLogoFile,
            })
          }
        >
          {t("outputs.save")}
        </Button>
        {displaySetting.agecProductPageURL && (
          <Button
            variant="tertiary"
            style={{ textTransform: "uppercase" }}
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            href={displaySetting.agecProductPageURL}
            rightSection={<IconExternalLink size="1rem" />}
          >
            {t("outputs.showExample")}
          </Button>
        )}
      </Group>
    </Stack>
  )
}
